/**
 * OracleGuideOnSidePluginContent
 *
 * This is the React component rendered inside the video.js plugin.
 * Unlike the original OracleGuideOnSide, this component:
 * - Receives player, contentId, featureMediaId as props (not from hooks)
 * - Gets JWT from Redux (reactive to auth changes)
 * - Does NOT use metaVisible from Redux (CSS handles visibility via vjs-user-active)
 * - Does NOT use 500ms interval for activity reporting (video.js handles this)
 * - Uses event-based activity reporting for sustained typing
 */

import React, {
  useCallback, useEffect, useLayoutEffect, useRef, useState, useMemo,
} from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useApolloClient } from '@apollo/client'
import { fromJS } from 'immutable'
import { usePostHog } from 'posthog-js/react'
import { isIOS } from '../../environment/index'
import { getBoundActions } from '../../actions/index'
import { selectAuth } from '../../services/auth/selectors'
import { selectPrimaryLanguage } from '../../services/user/selectors'
import {
  EN, DE, DE_DE, ES, ES_LA, FR, FR_FR,
} from '../../services/languages/constants'
import { selectOracleGuideOnSideClosed, selectOracleToastDismissedContentId } from '../../services/oracle/selectors'
import { setOracleGuideOnSideClosed, setOracleGuideEnabled } from '../../services/oracle/actions'
import { selectAppViewportWidth, selectAppViewportHeight } from '../../services/app/selectors'
import { get as getConfig } from '../../config/index'
import {
  AI_CONVERSATION_STARTED_GA4_EVENT,
  AI_CONVERSATION_RESUMED_GA4_EVENT,
  AI_MESSAGE_SENT_GA4_EVENT,
  AI_MESSAGE_RECEIVED_GA4_EVENT,
  AI_MESSAGE_FAILURE_GA4_EVENT,
  AI_ERROR_RESPONSE_GA4_EVENT,
  AI_MODULE_OPENED_GA4_EVENT,
  AI_MODULE_CLOSED_GA4_EVENT,
  AI_MODULE_VIEWED_GA4_EVENT,
  AI_MODULE_CLICKED_GA4_EVENT,
} from '../../services/event-tracking/index'
import {
  EventContextProvider,
  POSTHOG_FEATURE_FLAGS,
  usePostHogEvent,
} from '../PostHog/index'
import { OracleGuideOnSideLayout } from './OracleGuideOnSideLayout'
import { OracleGuideToastNudge } from './OracleGuideToastNudge'
import { useGotsChatSubscription, type GotsSubscriptionDocuments } from './hooks'
import { useConceptMoments } from './hooks/useConceptMoments'
import { useConceptTrigger } from './hooks/useConceptTrigger'
import { useGetChatSessionByVideoLazyQuery } from './queries.generated'
import { cacheStreamedMessage } from './cacheStreamedMessage'
import { useIntersectionObserver } from '../OracleCommons/useIntersectionObserver'
import {
  StaticTextProvider,
  InternationalStrings,
  useTranslatedText,
} from '../OracleCommons/StaticTextContext'
import type { OracleGuideOnSideContentProps } from '../../video-js/oracle-guide-on-side/types'

// Import language files for GotS-specific translations
import langEn from './lang_en.json'
import langDe from './lang_de-DE.json'
import langEs from './lang_es-LA.json'
import langFr from './lang_fr-FR.json'

const SMALL_DESKTOP_MAX_WIDTH = 1100

/**
 * Get the appropriate static text based on user language
 */
const getStaticTextForLanguage = (language: string | null): InternationalStrings => {
  switch (language) {
    case DE:
    case DE_DE:
      return fromJS({ data: langDe }) as InternationalStrings
    case ES:
    case ES_LA:
      return fromJS({ data: langEs }) as InternationalStrings
    case FR:
    case FR_FR:
      return fromJS({ data: langFr }) as InternationalStrings
    case EN:
    default:
      return fromJS({ data: langEn }) as InternationalStrings
  }
}

// TODO(gabe): Extract personality constants from personalityFilter.ts to
// lib/components/OracleCommons/ so both OraclePage and GotS can share them.
const TMP_PERSONALITY_ID = 2

/**
 * Props for the inner component (same as plugin props)
 */
type OracleGuideOnSideInnerProps = OracleGuideOnSideContentProps

/**
 * Inner component that handles all GotS logic and PostHog event tracking.
 * Must be rendered inside EventContextProvider to use usePostHogEvent.
 *
 * Key differences from original:
 * - player, contentId, featureMediaId come from props (plugin options)
 * - JWT comes from Redux (reactive to auth changes)
 * - No metaVisible - CSS handles visibility via .vjs-user-active
 * - No 500ms activity interval - uses event-based approach
 */
const OracleGuideOnSideInner: React.FC<OracleGuideOnSideInnerProps> = ({
  player,
  contentId,
  featureMediaId,
}) => {
  const dispatch = useDispatch()
  const reduxIsClosed = useSelector(selectOracleGuideOnSideClosed)

  // Check if the viewport is in mobile portrait orientation
  // Uses the established Gaia breakpoint ($bp-medium = 819px)
  const viewportWidth = useSelector(selectAppViewportWidth)
  const viewportHeight = useSelector(selectAppViewportHeight)
  const isMobilePortrait = viewportWidth < 819 && viewportHeight > viewportWidth

  const isClosed = reduxIsClosed

  // Track whether the panel has been opened at least once.
  // Until it has, we skip rendering OracleGuideOnSideLayout entirely
  // so there is no DOM element for CSS transitions to flash on load.
  const hasBeenOpenedRef = useRef(false)
  if (!isClosed) {
    hasBeenOpenedRef.current = true
  }

  // Track whether the user has explicitly dismissed the panel (X / Escape).
  // When true, entering fullscreen will NOT auto-open the guide.
  const userDismissedRef = useRef(false)

  // Get JWT from Redux auth state (reactive to auth changes)
  const auth = useSelector(selectAuth)
  const jwt = auth.get('jwt') || ''

  // Get GA4 event action
  const { setAiGa4Event } = getBoundActions(dispatch).eventTracking

  // Type-safe event tracking hooks
  const trackModuleOpened = usePostHogEvent('ai_module_opened')
  const trackModuleClosed = usePostHogEvent('ai_module_closed')
  const trackModuleViewed = usePostHogEvent('ai_module_viewed')
  const trackMessageReceived = usePostHogEvent('ai_message_received')
  const trackVideoStarted = usePostHogEvent('video_started')
  const trackMessageSent = usePostHogEvent('ai_message_sent')
  const trackErrorResponse = usePostHogEvent('ai_error_response')
  const trackMessageFailure = usePostHogEvent('ai_message_failure')
  const trackConversationStarted = usePostHogEvent('ai_conversation_started')
  const trackConversationResumed = usePostHogEvent('ai_conversation_resumed')
  const trackModerationViolation = usePostHogEvent('ai_moderation_violation')
  const trackModuleClicked = usePostHogEvent('ai_module_clicked')
  const trackToastShown = usePostHogEvent('concept_moment_toast_shown')
  const trackToastClicked = usePostHogEvent('concept_moment_toast_clicked')
  const trackToastDismissed = usePostHogEvent('concept_moment_toast_dismissed')

  // Fallback prompt text for toast action when moment data isn't available
  const fallbackPrompt = useTranslatedText({ keys: ['toastNudge', 'fallbackPrompt'] })

  // Track whether this is the initial render (vs a re-open)
  const hasOpenedBeforeRef = useRef(false)
  // Track when the guide was opened for session_duration_ms calculation
  const openedAtRef = useRef<number | null>(null)
  // Track previous video contentId to detect video changes for video_started event
  const prevContentIdRef = useRef<string | null>(null)
  // Track whether conversation started/resumed event has fired (fires once per session)
  const conversationEventFiredRef = useRef(false)
  // Track whether ai_module_viewed event has fired (should only fire once per page load)
  const moduleViewedFiredRef = useRef(false)

  // Apollo client and language for cache updates
  const apolloClient = useApolloClient()
  const language = useSelector(selectPrimaryLanguage) || EN

  // Track ai_module_opened event on every open transition. Auto-open
  // on initial render fires with is_initial: true; manual re-opens fire
  // with is_initial: false.
  useEffect(() => {
    if (!isClosed) {
      const isInitial = !hasOpenedBeforeRef.current
      hasOpenedBeforeRef.current = true
      openedAtRef.current = Date.now()

      // PostHog tracking
      trackModuleOpened('Video Guide', isInitial)
      // GA4 tracking
      setAiGa4Event(AI_MODULE_OPENED_GA4_EVENT.mergeDeep({
        is_initial: isInitial,
      }))
    }
  }, [isClosed, trackModuleOpened, setAiGa4Event])

  // Auto-open the guide on mobile after 1 second
  const hasAutoOpenedRef = useRef(false)
  useEffect(() => {
    if (!isMobilePortrait || !player || hasAutoOpenedRef.current) {
      return undefined
    }
    hasAutoOpenedRef.current = true
    const timerId = setTimeout(() => {
      dispatch(setOracleGuideOnSideClosed(false))
    }, 1000)
    return () => { clearTimeout(timerId) }
  }, [isMobilePortrait, player, dispatch])

  // Auto-open the guide when entering fullscreen, close when exiting.
  // In inline mode the guide panel is hidden — the user opens it via the
  // detail-page action button. In fullscreen the panel should be visible
  // by default (matching the pre-inline behaviour).
  // If the user has explicitly dismissed the panel (X / Escape), skip the
  // auto-open so it stays closed across fullscreen transitions.
  useEffect(() => {
    if (!player) return undefined
    const handleFullscreenChange = () => {
      const isFullscreen = !!(
        document.fullscreenElement
        || (document as unknown as Record<string, unknown>).webkitFullscreenElement
      )
      if (isFullscreen && !userDismissedRef.current) {
        dispatch(setOracleGuideOnSideClosed(false))
      } else if (!isFullscreen) {
        dispatch(setOracleGuideOnSideClosed(true))
      }
    }
    player.on('fullscreenchange', handleFullscreenChange)
    return () => {
      player.off('fullscreenchange', handleFullscreenChange)
    }
  }, [player, dispatch])

  // Add CSS class when component mounts (user is eligible to see guide)
  // Remove on unmount. This controls control bar button visibility.
  useEffect(() => {
    if (!player) return undefined
    player.addClass('vjs-oracle-guide-enabled')
    return () => {
      player.removeClass('vjs-oracle-guide-enabled')
    }
  }, [player])

  // Toggle CSS class on player for control bar button visibility
  // Button should be visible when panel is closed, hidden when panel is open.
  // useLayoutEffect prevents a flash of the open panel on first render —
  // the class is applied synchronously before the browser paints.
  useLayoutEffect(() => {
    if (!player) return
    if (isClosed) {
      player.addClass('vjs-oracle-guide-closed')
    } else {
      player.removeClass('vjs-oracle-guide-closed')
    }
  }, [player, isClosed])

  // Toggle portrait drawer class based on the videoDetailInlinePlayer config flag.
  // When enabled, the guide renders as a bottom sheet drawer in portrait mobile.
  // When disabled, the guide uses its default side panel layout.
  const isPortraitDrawerEnabled = useMemo(() => {
    const innerConfig = getConfig()
    return !!innerConfig?.features?.videoDetailInlinePlayer && isMobilePortrait
  }, [isMobilePortrait])

  useEffect(() => {
    if (!player) return undefined
    if (isPortraitDrawerEnabled) {
      player.addClass('vjs-portrait-drawer')
    } else {
      player.removeClass('vjs-portrait-drawer')
    }
    return () => {
      player.removeClass('vjs-portrait-drawer')
    }
  }, [player, isPortraitDrawerEnabled])

  // Lock page scroll when the portrait drawer is open.
  // Prevents the detail page content behind the drawer from scrolling —
  // only the AI Guide chat inside the drawer should be scrollable.
  //
  // IMPORTANT: Do NOT use position:fixed on document.body here.
  // On iOS Safari, position:fixed on body conflicts with the virtual
  // keyboard — the keyboard flashes open then immediately closes.
  useEffect(() => {
    if (isPortraitDrawerEnabled && !isClosed) {
      document.documentElement.style.overflow = 'hidden'
      document.body.style.overflow = 'hidden'
    } else {
      document.documentElement.style.overflow = ''
      document.body.style.overflow = ''
    }
    return () => {
      document.documentElement.style.overflow = ''
      document.body.style.overflow = ''
    }
  }, [isPortraitDrawerEnabled, isClosed])

  // On iOS Safari the virtual keyboard doesn't resize the layout viewport, so
  // we shrink the drawer height so the input stays above the keyboard.
  // We set an explicit height instead of adjusting bottom — adjusting bottom
  // causes iOS to scroll the visual viewport and the drawer covers the video.
  // On Android (which natively resizes), keyboardOffset stays near 0 and
  // this handler is effectively a no-op.
  //
  // On iOS, getBoundingClientRect() returns visual-viewport-relative values
  // when the keyboard is open, which double-compensates and produces
  // zero/negative height (the original "drawer disappears" bug). We use
  // getComputedStyle().top instead, which resolves the CSS calc() to stable
  // layout-viewport pixels unaffected by the keyboard.
  useEffect(() => {
    if (!isPortraitDrawerEnabled || !player) return undefined
    const vv = window.visualViewport
    if (!vv) return undefined

    const iosDevice = isIOS()

    const handleResize = () => {
      const drawerEl = player.el()?.querySelector('.vjs-oracle-guide-on-side') as HTMLElement | null
      if (!drawerEl) return
      const keyboardOffset = window.innerHeight - vv.height - vv.offsetTop
      if (keyboardOffset > 100) {
        if (iosDevice) {
          const topPx = parseFloat(getComputedStyle(drawerEl).top) || 0
          const visibleBottom = vv.height + vv.offsetTop
          const targetHeight = visibleBottom - topPx
          drawerEl.style.height = `${Math.max(0, targetHeight)}px`
        } else {
          drawerEl.style.height = ''
          const cssHeight = drawerEl.getBoundingClientRect().height
          drawerEl.style.height = `${cssHeight - keyboardOffset}px`
        }
      } else {
        drawerEl.style.height = ''
      }
    }

    vv.addEventListener('resize', handleResize)
    return () => {
      vv.removeEventListener('resize', handleResize)
      const drawerEl = player.el()?.querySelector('.vjs-oracle-guide-on-side') as HTMLElement | null
      if (drawerEl) {
        drawerEl.style.height = ''
      }
    }
  }, [isPortraitDrawerEnabled, player])

  // Track video_started when video changes (new video loaded)
  useEffect(() => {
    if (contentId && contentId !== prevContentIdRef.current) {
      // Only fire if this is a new video (not initial mount without video)
      if (prevContentIdRef.current !== null) {
        trackVideoStarted()
      }
      prevContentIdRef.current = contentId
    }
  }, [contentId, trackVideoStarted])

  // Chat session ID - either from lookup or newly created
  const [chatSessionId, setChatSessionId] = useState<string | null>(null)

  // Scroll indicator state - managed by Content, displayed by Layout
  const [showScrollIndicator, setShowScrollIndicator] = useState(false)
  const scrollToBottomRef = useRef<(() => void) | null>(null)

  // Error toast state
  const [showErrorToast, setShowErrorToast] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  // --- Toast Nudge (Dense Moments) ---
  // Fetch conceptually dense moments for the current video (gated by feature flag)
  const { moments: conceptMoments } = useConceptMoments()

  // Toast dismissal state from Redux
  const dismissedContentId = useSelector(selectOracleToastDismissedContentId)
  const isDismissed = dismissedContentId === contentId

  // Concept trigger evaluates playback time against moment startSeconds
  const {
    shouldShowToast,
    activeMoment,
    dismiss: dismissToast,
  } = useConceptTrigger({
    moments: conceptMoments,
    isGuideOpen: !isClosed,
    isDismissed,
  })

  // Track toast shown event (fire each time a new moment triggers the toast)
  const prevShownMomentRef = useRef<number | null>(null)
  useEffect(() => {
    if (shouldShowToast && activeMoment
        && prevShownMomentRef.current !== activeMoment.startSeconds) {
      prevShownMomentRef.current = activeMoment.startSeconds
      trackModuleViewed('Toast Message')
      trackToastShown(activeMoment.startSeconds, activeMoment.conceptLabel)
      setAiGa4Event(AI_MODULE_VIEWED_GA4_EVENT.mergeDeep({
        ui_element: 'Toast Message',
      }))
    }
  }, [shouldShowToast, activeMoment, trackModuleViewed, trackToastShown, setAiGa4Event])

  // Reset tracking ref on video change
  useEffect(() => {
    prevShownMomentRef.current = null
  }, [contentId])

  // Lazy query for looking up existing session by video
  const [lookupSession, { data: sessionData }] = useGetChatSessionByVideoLazyQuery()

  // Lookup existing session when video loads or changes
  useEffect(() => {
    if (!contentId || !featureMediaId) {
      setChatSessionId(null)
      return
    }

    lookupSession({
      variables: {
        contentId,
        featureMediaId,
      },
      context: { jwt },
    })
  }, [contentId, featureMediaId, lookupSession, jwt])

  // Set session ID when lookup completes
  useEffect(() => {
    const foundSession = sessionData?.oracleChatSessionList?.oracleChatSessions?.[0]

    if (foundSession?.id) {
      setChatSessionId(foundSession.id)
    } else if (sessionData && !foundSession) {
      setChatSessionId(null)
    }
  }, [sessionData])

  // Handle streaming completion - update Apollo cache
  const handleMessageComplete = useCallback((data: {
    messagePairId: string
    chatSessionId: string
    sessionTitle?: string | null
    userMessage: string
    oracleMessage: string
    documents: GotsSubscriptionDocuments | null
  }) => {
    // PostHog tracking
    trackMessageReceived(data.messagePairId, data.oracleMessage.length, 'Text')

    // GA4 tracking
    setAiGa4Event(AI_MESSAGE_RECEIVED_GA4_EVENT.mergeDeep({
      message_pair_id: data.messagePairId,
      response_length: data.oracleMessage.length,
    }))

    cacheStreamedMessage({
      apolloClient,
      chatSessionId: data.chatSessionId,
      messagePairId: data.messagePairId,
      userMessage: data.userMessage,
      oracleMessage: data.oracleMessage,
      documents: data.documents,
      language,
    })
  }, [apolloClient, language, trackMessageReceived, setAiGa4Event])

  // Chat subscription hook
  const {
    streamingText,
    isStreaming,
    pendingMessage,
    sendMessage: sendMessageToSubscription,
    stopStreaming,
    documents: streamingDocuments,
    moderationError,
  } = useGotsChatSubscription({
    personalityId: TMP_PERSONALITY_ID.toString(),
    jwt,
    chatSessionId,
    contentId,
    featureMediaId,
    language,
    onSessionReceived: (sessionId) => setChatSessionId(sessionId),
    onMessageComplete: handleMessageComplete,
    onError: (err) => {
      setErrorMessage(err.message)
      setShowErrorToast(true)
    },
    onBackendError: (errorCode, errMsg) => {
      // PostHog tracking
      trackErrorResponse(errMsg, 'Text', 'Text Field')
      // GA4 tracking
      setAiGa4Event(AI_ERROR_RESPONSE_GA4_EVENT.mergeDeep({
        error_code: errorCode,
        error_message: errMsg,
      }))
    },
    onClientError: (errMsg) => {
      // PostHog tracking
      trackMessageFailure(errMsg, 'Text', 'Text Field')
      // GA4 tracking
      setAiGa4Event(AI_MESSAGE_FAILURE_GA4_EVENT.mergeDeep({
        error_message: errMsg,
      }))
    },
    onModerationError: (categories, severity) => {
      trackModerationViolation(severity, categories)
    },
  })

  /**
   * Wrapped sendMessage that combines analytics tracking with message sending.
   */
  const sendMessage = useCallback((message: string) => {
    // Fire conversation started/resumed on first message only
    if (!conversationEventFiredRef.current) {
      conversationEventFiredRef.current = true
      if (chatSessionId) {
        // PostHog tracking
        trackConversationResumed('Text', 'Text Field')
        // GA4 tracking
        setAiGa4Event(AI_CONVERSATION_RESUMED_GA4_EVENT)
      } else {
        // PostHog tracking
        trackConversationStarted('Text', 'Video Guide')
        // GA4 tracking
        setAiGa4Event(AI_CONVERSATION_STARTED_GA4_EVENT.mergeDeep({
          ui_element: 'Text Field',
        }))
      }
    }

    // PostHog tracking
    trackMessageSent(message, 'Text', 'Text Field')
    // GA4 tracking
    setAiGa4Event(AI_MESSAGE_SENT_GA4_EVENT.mergeDeep({
      ui_element: 'Text Field',
      input_method: 'text sent',
    }))

    sendMessageToSubscription(message)
  }, [
    chatSessionId,
    trackMessageSent,
    trackConversationStarted,
    trackConversationResumed,
    sendMessageToSubscription,
    setAiGa4Event,
  ])

  // Ref to the chat container for focus containment checking
  const chatContainerRef = useRef<HTMLDivElement | null>(null)
  // Ref to store cleanup function for keydown listener
  const keydownCleanupRef = useRef<(() => void) | null>(null)
  // Outer container element for mouse activity tracking
  // Use state instead of ref so we can re-run the effect when the element is attached
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null)

  // Wrapped callback that tracks both PostHog and GA4
  // Only fires once per page load (not on re-open)
  const handleModuleViewed = useCallback(() => {
    if (moduleViewedFiredRef.current) return
    moduleViewedFiredRef.current = true

    // PostHog tracking
    trackModuleViewed('Video Guide')
    // GA4 tracking
    setAiGa4Event(AI_MODULE_VIEWED_GA4_EVENT)
  }, [trackModuleViewed, setAiGa4Event])

  // Track ai_module_viewed when container becomes visible in viewport
  // Note: Always enabled, but handleModuleViewed only fires once per page load via ref check
  useIntersectionObserver(chatContainerRef, handleModuleViewed, { enabled: true })

  // Mouse event activity reporting - keeps GotS visible while interacting
  // This ensures the video.js player doesn't auto-hide controls when the user
  // is hovering over or scrolling through the GotS panel
  useEffect(() => {
    if (!containerEl || !player?.reportUserActivity) return undefined

    const reportActivity = () => {
      player.reportUserActivity()
    }

    // Report activity on mouse movement, scrolling, and entry
    containerEl.addEventListener('mousemove', reportActivity)
    containerEl.addEventListener('wheel', reportActivity)
    containerEl.addEventListener('mouseenter', reportActivity)

    return () => {
      containerEl.removeEventListener('mousemove', reportActivity)
      containerEl.removeEventListener('wheel', reportActivity)
      containerEl.removeEventListener('mouseenter', reportActivity)
    }
  }, [player, containerEl])

  /**
   * Event-based activity reporting for sustained typing.
   *
   * Unlike the original 500ms interval approach, this listens for keydown
   * events and reports activity on each keypress. This is more efficient
   * and integrates better with video.js.
   */
  const handleContainerFocus = useCallback(() => {
    if (!player?.reportUserActivity) return

    // Report activity immediately on focus
    player.reportUserActivity()

    // Set up keydown listener for sustained typing
    const handleKeyDown = () => {
      player.reportUserActivity()
    }

    document.addEventListener('keydown', handleKeyDown)
    keydownCleanupRef.current = () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [player])

  /**
   * Clean up keydown listener when focus leaves the container.
   * Uses requestAnimationFrame to handle focus moving between elements.
   */
  const handleContainerBlur = useCallback(() => {
    requestAnimationFrame(() => {
      // Check if focus is still within the chat container
      if (chatContainerRef.current?.contains(document.activeElement)) {
        return // Focus moved to another element within the chat area
      }
      // Focus truly left, clean up
      if (keydownCleanupRef.current) {
        keydownCleanupRef.current()
        keydownCleanupRef.current = null
      }
    })
  }, [])

  // Clean up keydown listener when panel is closed or component unmounts
  useEffect(() => {
    if (isClosed && keydownCleanupRef.current) {
      keydownCleanupRef.current()
      keydownCleanupRef.current = null
    }
    return () => {
      if (keydownCleanupRef.current) {
        keydownCleanupRef.current()
        keydownCleanupRef.current = null
      }
    }
  }, [isClosed])

  /**
   * Handle toast nudge action ("Break that last part down" clicked).
   * Opens the guide panel and sends a contextual prompt through the chat
   * subscription. This follows the same pattern as the generated prompt
   * suggestions -- sendMessage() persists the message pair to the DB
   * via the backend and streams the AI response.
   */
  const handleToastAction = useCallback(() => {
    // Build a prompt enriched with the concept label so the LLM focuses
    // on the specific dense section.  Fall back to generic static text
    // if moment data isn't available yet.
    const prompt = activeMoment
      ? `Dive deeper on ${activeMoment.conceptLabel}`
      : fallbackPrompt

    // PostHog tracking
    trackModuleClicked('Toast Message')
    if (activeMoment) {
      trackToastClicked(activeMoment.startSeconds, activeMoment.conceptLabel)
    }
    // GA4 tracking
    setAiGa4Event(AI_MODULE_CLICKED_GA4_EVENT.mergeDeep({
      ui_element: 'Toast Message',
    }))

    // Open the guide panel
    dispatch(setOracleGuideOnSideClosed(false))

    // Hide toast immediately (bypasses Redux round-trip)
    dismissToast()

    // Send the prompt through the chat subscription.
    // This persists the user message + AI response as a message pair in the DB
    // and streams the response back into the guide (same as generated prompts).
    if (prompt) {
      sendMessage(prompt)
    }
  }, [
    activeMoment, fallbackPrompt, sendMessage,
    dispatch, dismissToast, trackModuleClicked, trackToastClicked, setAiGa4Event,
  ])

  /**
   * Handle toast nudge dismiss (close button).
   * Only dismisses the current moment — future moments will still trigger.
   * The dismiss() from useConceptTrigger marks this specific moment so it
   * won't re-show on seek-back, but other moments remain unaffected.
   */
  const handleToastDismiss = useCallback((method: 'manual' | 'auto') => {
    // PostHog tracking
    trackModuleClosed(null, 'Toast Message', method)
    if (activeMoment) {
      trackToastDismissed(activeMoment.startSeconds, activeMoment.conceptLabel)
    }
    // GA4 tracking
    setAiGa4Event(AI_MODULE_CLOSED_GA4_EVENT.mergeDeep({
      ui_element: 'Toast Message',
      method,
    }))

    // Hide toast and mark only this moment as dismissed (not the whole video)
    dismissToast()
  }, [activeMoment, dismissToast, trackModuleClosed, trackToastDismissed, setAiGa4Event])

  /**
   * Close the Guide panel. Called when user presses Escape or clicks X.
   */
  const handleClose = useCallback(() => {
    const sessionDurationMs = openedAtRef.current
      ? Date.now() - openedAtRef.current
      : null

    // PostHog tracking
    trackModuleClosed(sessionDurationMs, 'Video Guide')
    // GA4 tracking
    setAiGa4Event(AI_MODULE_CLOSED_GA4_EVENT.mergeDeep({
      session_duration_ms: sessionDurationMs,
    }))
    userDismissedRef.current = true
    dispatch(setOracleGuideOnSideClosed(true))
  }, [dispatch, trackModuleClosed, setAiGa4Event])

  /**
   * Handle scroll indicator visibility changes from Content component.
   */
  const handleScrollIndicatorChange = useCallback((
    visible: boolean,
    scrollToBottom: () => void,
  ) => {
    setShowScrollIndicator(visible)
    scrollToBottomRef.current = scrollToBottom
  }, [])

  // Portal the toast into the video.js player element so it is NOT inside
  // the .vjs-oracle-guide-on-side container (which is hidden when the panel is closed).
  const playerEl = player?.el?.() as HTMLElement | null

  return (
    <>
      {playerEl && createPortal(
        <OracleGuideToastNudge
          isVisible={shouldShowToast}

          onAction={handleToastAction}
          onDismiss={handleToastDismiss}
        />,
        playerEl,
      )}
      {hasBeenOpenedRef.current && (
        <OracleGuideOnSideLayout
          // Note: metaVisible and controlsVisible are no longer needed
          // CSS handles visibility via .vjs-user-active
          streamingText={streamingText}
          isStreaming={isStreaming}
          pendingMessage={pendingMessage}
          sendMessage={sendMessage}
          stopStreaming={stopStreaming}
          onContainerFocus={handleContainerFocus}
          onContainerBlur={handleContainerBlur}
          containerRef={chatContainerRef}
          chatSessionId={chatSessionId}
          onClose={handleClose}
          showScrollIndicator={showScrollIndicator}
          onScrollToBottom={() => scrollToBottomRef.current?.()}
          onScrollIndicatorChange={handleScrollIndicatorChange}
          showErrorToast={showErrorToast}
          errorMessage={errorMessage}
          onErrorToastClose={() => {
            setShowErrorToast(false)
            setErrorMessage(undefined)
          }}
          outerContainerRef={setContainerEl}
          moderationError={moderationError}
          player={player}
          streamingDocuments={streamingDocuments}
          activeMoment={activeMoment}
        />
      )}
    </>
  )
}

/**
 * Root component for Guide on Side (Plugin version)
 *
 * This is the component that gets rendered by the video.js plugin.
 * It wraps the inner component with necessary providers.
 */
export const OracleGuideOnSidePluginContent: React.FC<OracleGuideOnSideContentProps> = (props) => {
  const dispatch = useDispatch()
  const posthog = usePostHog()
  const language = useSelector(selectPrimaryLanguage)
  const config = getConfig()
  const viewportWidth = useSelector(selectAppViewportWidth)
  const viewportHeight = useSelector(selectAppViewportHeight)
  const isMobilePortrait = viewportWidth < 819 && viewportHeight > viewportWidth
  const isSmallDesktop = !isMobilePortrait && viewportWidth < SMALL_DESKTOP_MAX_WIDTH

  // Track if PostHog flags are ready (avoids race conditions)
  const [flagsReady, setFlagsReady] = useState(false)

  // Memoize static text to avoid recreating on every render
  const staticText = useMemo(
    () => getStaticTextForLanguage(language),
    [language],
  )

  // Master switch: is the feature enabled at all?
  const isConfigEnabled = config?.features?.ai?.oracleGuideOnTheSide ?? false
  const isPostHogEnabled = config?.features?.posthog ?? false

  // Wait for PostHog flags to load before checking experiments
  useEffect(() => {
    if (!posthog || !isPostHogEnabled) {
      setFlagsReady(true) // No PostHog or disabled, proceed immediately
      return
    }

    posthog.onFeatureFlags(() => {
      setFlagsReady(true) // Flags loaded, safe to check
    })
  }, [posthog, isPostHogEnabled])

  if (!isConfigEnabled) {
    return null // Feature disabled via config
  }

  // Memoize flag checks to avoid re-checking on every render
  // Only re-check if posthog instance or flagsReady state changes
  const isInExperiment = useMemo(() => {
    if (!isPostHogEnabled || !flagsReady) {
      return false
    }

    // Legacy flag grants access on both desktop and mobile (used for employees)
    const isEnabledLegacy = posthog?.isFeatureEnabled(POSTHOG_FEATURE_FLAGS.AI_GOTS) ?? false

    if (isMobilePortrait) {
      // Mobile web: gate on mobile experiment flags
      const {
        AI_GUIDE_MOBILE_SEGMENT_A, AI_GUIDE_MOBILE_SEGMENT_B,
        AI_GUIDE_MOBILE_SEGMENT_C, AI_GUIDE_MOBILE_SEGMENT_D,
      } = POSTHOG_FEATURE_FLAGS
      const mobileAVariant = posthog?.getFeatureFlag(AI_GUIDE_MOBILE_SEGMENT_A)
      const mobileBVariant = posthog?.getFeatureFlag(AI_GUIDE_MOBILE_SEGMENT_B)
      const mobileCVariant = posthog?.getFeatureFlag(AI_GUIDE_MOBILE_SEGMENT_C)
      const mobileDVariant = posthog?.getFeatureFlag(AI_GUIDE_MOBILE_SEGMENT_D)
      return (
        isEnabledLegacy
        || mobileAVariant === 'test'
        || mobileBVariant === 'test'
        || mobileCVariant === 'test'
        || mobileDVariant === 'test'
      )
    }

    // Desktop: gate on desktop experiment flags
    const segmentAVariant = posthog?.getFeatureFlag(POSTHOG_FEATURE_FLAGS.AI_GUIDE_SEGMENT_A)
    const segmentBVariant = posthog?.getFeatureFlag(POSTHOG_FEATURE_FLAGS.AI_GUIDE_SEGMENT_B)
    const segmentCVariant = posthog?.getFeatureFlag(POSTHOG_FEATURE_FLAGS.AI_GUIDE_SEGMENT_C)
    const segmentDVariant = posthog?.getFeatureFlag(POSTHOG_FEATURE_FLAGS.AI_GUIDE_SEGMENT_D)

    return (
      isEnabledLegacy
      || segmentAVariant === 'test'
      || segmentBVariant === 'test'
      || segmentCVariant === 'test'
      || segmentDVariant === 'test'
    )
  }, [posthog, flagsReady, isPostHogEnabled, isMobilePortrait])

  // Compute whether user is eligible to see the guide
  // This combines Gaia config flag with PostHog experiment flags
  const isGuideEnabled = isConfigEnabled && (!isPostHogEnabled || isInExperiment)

  // Dispatch guideEnabled state to Redux so other components (sidebar button, control bar button)
  // can access it without duplicating the PostHog flag logic
  useEffect(() => {
    // Only dispatch once flags are ready (or if PostHog is disabled)
    if (!flagsReady && isPostHogEnabled) return
    dispatch(setOracleGuideEnabled(isGuideEnabled && !isSmallDesktop))
  }, [dispatch, isGuideEnabled, isSmallDesktop, flagsReady, isPostHogEnabled])

  // Wait for flags to load or check if user is not in experiment
  if (!isGuideEnabled || isSmallDesktop) {
    return null
  }

  // In inline mode (mobile portrait + videoDetailInlinePlayer config flag),
  // the AppDrawer in DetailVideoPlayer handles the guide UI. The plugin
  // still initializes so guideEnabled is dispatched to Redux, but we
  // return null here to avoid rendering duplicate UI.
  const isInlineMode = !!config?.features?.ai?.oracleInlineGuide && isMobilePortrait
  if (isInlineMode) {
    return null
  }

  return (
    <StaticTextProvider staticText={staticText}>
      <EventContextProvider context="ai_guide_on_the_side">
        <OracleGuideOnSideInner {...props} />
      </EventContextProvider>
    </StaticTextProvider>
  )
}

export default OracleGuideOnSidePluginContent
